<template>
  <div v-if="mascots.length > 0" class="mascot-display" :class="containerClass">
    <div
      v-for="(mascot, index) in mascots"
      :key="mascot.id"
      class="mascot-item"
      :class="getMascotClass(mascot, index)"
    >
      <img
        :src="getMascotImagePath(mascot)"
        :alt="mascot.name"
        class="mascot-image"
        @error="handleImageError"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { getMascotVariant, type Mascot } from '@/constants/mascots'
import { useMascots } from '@/composables/useMascots'

interface Props {
  mascotIds: string[]
  layout?: 'horizontal' | 'vertical' | 'grid'
  size?: 'small' | 'medium' | 'large'
  showHeightDifference?: boolean
  variant?: 'happy' | 'neutral' | 'worried'
  direction?: 'left' | 'right' | 'auto'
}

const props = withDefaults(defineProps<Props>(), {
  layout: 'horizontal',
  size: 'medium',
  showHeightDifference: true,
  variant: 'neutral',
  direction: 'auto',
})

// Use the mascots composable
const { getMascots, validateMascotIds } = useMascots()

// Get mascots and validate IDs
const mascots = computed(() => {
  const validation = validateMascotIds(props.mascotIds)
  if (validation.invalid.length > 0) {
    console.warn(`Invalid mascot IDs: ${validation.invalid.join(', ')}`)
  }
  return getMascots(validation.valid)
})

const containerClass = computed(() => ({
  [`mascot-${props.layout}`]: true,
  [`mascot-${props.size}`]: true,
  'show-height-difference': props.showHeightDifference,
  [`mascot-direction-${props.direction}`]: true,
}))

const getMascotClass = (mascot: Mascot, index: number) => ({
  'mascot-adult': mascot.isAdult,
  'mascot-child': !mascot.isAdult,
  [`mascot-${index}`]: true,
})

const getMascotImagePath = (mascot: Mascot): string => {
  return getMascotVariant(mascot.id, props.variant)
}

const handleImageError = (event: Event) => {
  const img = event.target as HTMLImageElement
  console.warn(`Failed to load mascot image: ${img.src}`)
  // You could set a fallback image here
  img.style.display = 'none'
}
</script>

<style scoped>
.mascot-display {
  display: flex;
  align-items: flex-end;
  gap: 10px;
  overflow: visible;
}

.mascot-horizontal {
  flex-direction: row;
}

.mascot-vertical {
  flex-direction: column;
  align-items: center;
}

.mascot-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(60px, 1fr));
  gap: 0;
  align-items: end;
}

.mascot-item {
  display: flex;
  align-items: flex-end;
  transition: transform 0.2s ease;
  overflow: visible;
  flex-shrink: 0;
}

.mascot-item:hover {
  transform: translateY(-2px);
}

.mascot-image {
  width: auto;
  height: auto;
  object-fit: contain;
  filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.1));
  flex-shrink: 0;
}

/* Size variations - Scale based on height while preserving original aspect ratio */
.mascot-small .mascot-image {
  max-height: 80px;
  width: auto;
}

.mascot-medium .mascot-image {
  max-height: 120px;
  width: auto;
}

.mascot-large .mascot-image {
  max-height: 160px;
  width: auto;
}

/* Height difference for adults vs children - Adults are taller but preserve original proportions */
.show-height-difference .mascot-adult .mascot-image {
  max-height: 1.3em; /* Adults are 30% taller than children */
  width: auto; /* Preserve original aspect ratio */
}

.show-height-difference .mascot-child .mascot-image {
  max-height: 1em; /* Base height for children */
  width: auto; /* Preserve original aspect ratio */
}

/* Specific height adjustments for each size - Preserve original aspect ratios */
.show-height-difference.mascot-small .mascot-adult .mascot-image {
  max-height: 104px; /* 80px * 1.3 */
  width: auto; /* Preserve original aspect ratio */
}

.show-height-difference.mascot-small .mascot-child .mascot-image {
  max-height: 80px;
  width: auto; /* Preserve original aspect ratio */
}

.show-height-difference.mascot-medium .mascot-adult .mascot-image {
  max-height: 156px; /* 120px * 1.3 */
  width: auto; /* Preserve original aspect ratio */
}

.show-height-difference.mascot-medium .mascot-child .mascot-image {
  max-height: 120px;
  width: auto; /* Preserve original aspect ratio */
}

.show-height-difference.mascot-large .mascot-adult .mascot-image {
  max-height: 208px; /* 160px * 1.3 */
  width: auto; /* Preserve original aspect ratio */
}

.show-height-difference.mascot-large .mascot-child .mascot-image {
  max-height: 160px;
  width: auto; /* Preserve original aspect ratio */
}

/* No responsive adjustments - same size for desktop and mobile */

/* Direction classes - flip mascots to look toward content */
.mascot-direction-left .mascot-image {
  transform: scaleX(-1); /* Flip horizontally to look right */
}

.mascot-direction-right .mascot-image {
  transform: scaleX(1); /* Normal orientation to look left */
}

.mascot-direction-auto .mascot-image {
  transform: scaleX(1); /* Default: look left (original orientation) */
}

/* Grid layout adjustments */
.mascot-grid .mascot-item {
  justify-content: center;
}

.mascot-grid .mascot-image {
  width: 100%;
}
</style>
